/*
 * Copyright 2024 GLA UK Research and Development Directive
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *       http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { Injectable } from '@angular/core';
import { Router, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { KeycloakAuthGuard, KeycloakService } from 'keycloak-angular';

/**
 * The Auth Guard Service.
 */
@Injectable({
    providedIn: 'root'
})
export class AuthGuardService extends KeycloakAuthGuard  {

    /**
     * The Service Constructor.
     * 
     * @param router            The angular router
     * @param keycloakAngular   The keycloak service
     */
    constructor(protected router: Router,
                protected keycloak: KeycloakService) {
        super(router, keycloak);
    }

    /**
     * Evaluates whether a certain route should be accessible.
     * 
     * @param route The route to be evaluated
     * @param state The router state snapshot
     */
    async isAccessAllowed(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
        // Force the user to log in if currently unauthenticated.
        if (!this.authenticated) {
            await this.keycloak.login({
                redirectUri: window.location.origin + state.url
            });
        }
    
        // Get the roles required from the route.
        const requiredRoles = route.data.roles;
    
        // Allow the user to proceed if no additional roles are required to access the route.
        if (!Array.isArray(requiredRoles) || requiredRoles.length === 0) {
            return true;
        }
    
        // Allow the user to proceed if all the required roles are present.
        let isAllowed = requiredRoles.every((role) => this.roles.includes(role));
    
        if (isAllowed) {
            return true;
        } else {
            return false;
        }
    }
}
